import { DestinationCGType } from '@/types/graphql/root';
import React, { useRef, useState } from 'react';
import 'dayjs/locale/ko';
import { ReactSortable, Sortable } from 'react-sortablejs';
import ArrowRight232323812 from '@svg/trip/arrow_right_232323_8_12.svg';
import DestSlot from '@/app/ui/trip/dest-slot';
import AddGray1818 from '@svg/trip/add_gray_18_18.svg';
import { useAtom, useSetAtom } from 'jotai';
import { isEditRoleAtom } from '@/atom/trip';
import { usePathname } from 'next/navigation';
import { history } from '@/utils/history';
import { historyStackAtom } from '@/atom/historyStack';
import { useRouter } from 'next/navigation';
import { TRIP_CONDITIONS, TRIP_MODE, TripModeType } from '@/app/ui/trip/client-trip-page';
import { alertModalAtom } from '@/atom/modal';

export interface TripItineraryViewProps {
  destinationsByDay: {
    [key: number]: {
      morningAccomm?: {
        prev: DestinationCGType | null;
        cur: DestinationCGType;
        next: DestinationCGType | null;
      };
      nightAccomm?: {
        prev: DestinationCGType | null;
        cur: DestinationCGType;
        next: DestinationCGType | null;
      };
      destinationsWithoutAccomm: {
        prev: DestinationCGType | null;
        cur: DestinationCGType;
        next: DestinationCGType | null;
      }[];
    };
  };
  destinations: DestinationCGType[];
  focusedDay: number;
  memoDictionary: { [key: string]: string };
  selectedDestIdList: string[];
  isSortableDragging: boolean;
  mode: TripModeType;
  isEmbedded: boolean;
  getDayInfo: (day: number) => string | null;
  setDestinations: React.Dispatch<React.SetStateAction<DestinationCGType[]>>;
  onSortableDragEnd: (e: Sortable.SortableEvent) => Promise<void>;
  setIsSortableDragging: React.Dispatch<React.SetStateAction<boolean>>;
  onClickPoiDetail: (dest: DestinationCGType) => void;
  onClickChangeMode: (nextMode: TripModeType) => Promise<void>;
  setAddDestinationMode: React.Dispatch<React.SetStateAction<'selectDay' | 'normal'>>;
  setAddDestinationsPosition: React.Dispatch<
    React.SetStateAction<{
      day: number;
      pos: number;
      replace?: boolean | undefined;
      replaceDestId?: string | undefined;
    } | null>
  >;
  onClickRemoveSlot: (destId: string) => void;
  onToggleSelectDest: (destId: string) => void;
  onUpdateMemo: (destId: string, memo: string) => void;
  setSelectDayModalVisible: React.Dispatch<React.SetStateAction<boolean>>;
  실험실짱: boolean;
}

export default function TripItineraryView({
  destinationsByDay,
  destinations,
  focusedDay,
  memoDictionary,
  selectedDestIdList,
  isSortableDragging,
  mode,
  isEmbedded,
  getDayInfo,
  setDestinations,
  onSortableDragEnd,
  setIsSortableDragging,
  onClickPoiDetail,
  onClickChangeMode,
  setAddDestinationMode,
  setAddDestinationsPosition,
  onClickRemoveSlot,
  onToggleSelectDest,
  onUpdateMemo,
  setSelectDayModalVisible,
  실험실짱,
}: TripItineraryViewProps) {
  const setAlertModal = useSetAtom(alertModalAtom);

  const [isEditRole] = useAtom(isEditRoleAtom);
  const pathname = usePathname();
  const setHistoryStack = useSetAtom(historyStackAtom);
  const router = useRouter();

  const [isFinishItinerarySummary, setIsFinishItinerarySummary] = useState(false);
  const itinerarySummaryContainerRef = useRef<HTMLDivElement>(null);
  const itinerarySummaryRef = useRef<HTMLDivElement>(null);

  // 에러 모달 표시 헬퍼 함수
  const showErrorModal = (message: string) => {
    setAlertModal({
      visible: true,
      message,
      onConfirm: () => {
        setAlertModal({ visible: false });
      },
      onOuterClick: () => {
        setAlertModal({ visible: false });
      },
    });
  };

  // 컨테이너 닫기 헬퍼 함수
  const closeSummaryContainer = () => {
    if (itinerarySummaryContainerRef.current) {
      itinerarySummaryContainerRef.current.style.transition = 'none';
      itinerarySummaryContainerRef.current.style.overflow = 'hidden';
      itinerarySummaryContainerRef.current.style.height = '0px';
    }
    setIsFinishItinerarySummary(false);
  };

  // 마크다운을 HTML로 변환하는 함수
  const markdownToHtml = (text: string) => {
    return text.replace(/\*\*(.+?)\*\*/g, '<strong class="text-gray-800">$1</strong>').replace(/\n/g, '<br>');
  };

  const onClickItinerarySummaryHandler = async () => {
    // 최소 장소 개수 체크
    if (destinations.length < 2) {
      showErrorModal('여행장소를 2개 이상 추가해주세요.');
      return;
    }

    setIsFinishItinerarySummary(true);

    // 컨테이너 초기화 및 로딩 메시지 표시
    if (itinerarySummaryContainerRef.current && itinerarySummaryRef.current) {
      itinerarySummaryContainerRef.current.style.height = '40px';
      itinerarySummaryContainerRef.current.style.overflow = '';
      itinerarySummaryRef.current.innerHTML = `<div class="flex items-center justify-center gap-1 text-point-400"><span>일정표 요약 중 입니다</span><span class="flex gap-[0.15rem] items-center"><span class="w-[0.25rem] h-[0.25rem] bg-point-400 rounded-full animate-bounce [animation-delay:0ms]"></span><span class="w-[0.25rem] h-[0.25rem] bg-point-400 rounded-full animate-bounce [animation-delay:150ms]"></span><span class="w-[0.25rem] h-[0.25rem] bg-point-400 rounded-full animate-bounce [animation-delay:300ms]"></span></span></div>`;
    }

    // 타이핑 효과를 위한 변수
    let textBuffer = '';
    let displayedText = '';
    let typingInterval: number | null = null;
    let isFirstChunk = true;

    // 타이핑 애니메이션 함수
    const startTyping = () => {
      if (typingInterval) return;

      typingInterval = window.setInterval(() => {
        if (textBuffer.length > displayedText.length) {
          // 한 글자씩 추가
          displayedText += textBuffer[displayedText.length];

          if (itinerarySummaryRef.current) {
            itinerarySummaryRef.current.innerHTML = markdownToHtml(displayedText);

            // 높이 조절
            if (itinerarySummaryContainerRef.current) {
              itinerarySummaryContainerRef.current.style.height = itinerarySummaryRef.current.clientHeight + 'px';
            }
          }
        } else {
          // 모든 텍스트가 표시되면 타이핑 중지
          if (typingInterval) {
            clearInterval(typingInterval);
            typingInterval = null;
          }
        }
      }, 20);
    };

    try {
      // console.log(JSON.stringify({ destinations }, null, 2));
      const response = await fetch('/api/itinerary-summary', {
        method: 'POST',
        body: JSON.stringify({ destinations }),
      });

      // 응답 상태 체크
      if (!response.ok) {
        closeSummaryContainer();
        const errorData = await response.json().catch(() => ({
          message: '잠시 후 다시 시도해주세요.',
        }));
        showErrorModal(errorData.message || `오류가 발생했습니다. (${response.status})`);
        return;
      }

      // 스트리밍 응답 체크
      if (!response.body) {
        closeSummaryContainer();
        showErrorModal('응답을 받을 수 없습니다.');
        return;
      }

      // 스트리밍 처리
      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              if (data.text) {
                // 첫 번째 텍스트가 도착하면 로딩 메시지 제거
                if (isFirstChunk) {
                  displayedText = '';
                  textBuffer = '';
                  isFirstChunk = false;
                }

                // 버퍼에 텍스트 추가
                textBuffer += data.text;

                // 타이핑 애니메이션 시작
                if (!typingInterval) {
                  startTyping();
                }
              }
            } catch (e) {
              console.error('JSON 파싱 에러:', e);
            }
          }
        }
      }
    } catch (error) {
      closeSummaryContainer();
      showErrorModal('요약 생성 중 오류가 발생했습니다.');
      console.error('요약 생성 에러:', error);
    }
  };

  return (
    <div id='dest-container'>
      {Object.keys(destinationsByDay).map((dayNumber, dayIndex) => {
        const isLastDay = dayIndex === Object.keys(destinationsByDay).length - 1;
        const isEmptyDay =
          !destinationsByDay[+dayNumber].morningAccomm &&
          !destinationsByDay[+dayNumber].nightAccomm &&
          destinationsByDay[+dayNumber].destinationsWithoutAccomm.length === 0;

        const morningAccomm = destinationsByDay[+dayNumber].morningAccomm;
        const nightAccomm = destinationsByDay[+dayNumber].nightAccomm;
        const destListWithoutAccomms = destinationsByDay[+dayNumber].destinationsWithoutAccomm;

        return (
          <React.Fragment key={'dayBox_' + dayNumber}>
            <div
              className={`sticky bg-white z-[2] ${TRIP_CONDITIONS.IS_VIEW_OR_MEMO_MODE(mode) ? 'top-0' : 'top-52pxr'}`}
              style={{
                zIndex: 999 - +dayNumber,
              }}
            >
              <div
                id={'dayHeader_' + dayNumber}
                key={mode + '_dayHeader_' + dayNumber}
                className={`day-header-button flex flex-row items-center w-full py-10pxr px-20pxr z-[1] justify-between ${
                  TRIP_CONDITIONS.IS_VIEW_OR_MEMO_MODE(mode) ? 'bg-gray-100' : 'bg-point-100'
                }`}
              >
                <button
                  className='flex items-center h-22pxr'
                  onClick={() => {
                    if (history) {
                      history.push(pathname + '#select-day');
                      setHistoryStack((prev) => [
                        ...prev,
                        {
                          name: 'SelectDayModal 종료 (from client-trip-page.tsx)',
                          afterAction: () => setSelectDayModalVisible(false),
                        },
                      ]);
                      setSelectDayModalVisible(true);
                    }
                  }}
                >
                  {+dayNumber === 1 ? (
                    <>
                      <span className='sub-title-14-bold text-custom-black mr-6pxr'>{focusedDay}일차</span>
                      <span className='text-gray-700 body-14-regular mr-4pxr'>
                        {getDayInfo(+dayNumber + focusedDay - 1)}
                      </span>
                    </>
                  ) : (
                    <>
                      <span className='sub-title-14-bold text-custom-black mr-6pxr'>{dayNumber}일차</span>
                      <span className='text-gray-700 body-14-regular mr-4pxr'>{getDayInfo(+dayNumber)}</span>
                    </>
                  )}
                  <span className='w-20pxr h-20pxr flex items-center justify-center'>
                    <ArrowRight232323812 className='flex-none' />
                  </span>
                </button>
                {isEditRole && !isEmbedded && 1 === +dayNumber && !TRIP_CONDITIONS.IS_MEMO_MODE(mode) && (
                  <div className='flex items-center h-22pxr'>
                    <button
                      className='mr-12pxr sub-heading-14-m text-custom-black py-2pxr px-4pxr'
                      onClick={() => {
                        onClickChangeMode(TRIP_MODE.MEMO);
                      }}
                    >
                      메모
                    </button>
                    <div className='h-14pxr bg-gray-400 w-1pxr'></div>
                    <button
                      className='ml-12pxr sub-heading-14-m text-primary py-2pxr px-4pxr'
                      onClick={() => {
                        if (TRIP_CONDITIONS.IS_VIEW_MODE(mode)) {
                          onClickChangeMode(TRIP_MODE.EDIT);
                        } else {
                          router.back();
                        }
                      }}
                    >
                      {TRIP_CONDITIONS.IS_VIEW_MODE(mode) ? '편집' : '보기'}
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div
              id={`dayContainer_${dayNumber}`}
              className={`relative flex flex-col ${TRIP_CONDITIONS.IS_VIEW_MODE(mode) ? 'pt-20pxr pb-28pxr' : 'pt-24pxr pb-32pxr'}`}
              style={isLastDay ? { paddingBottom: 0 } : {}}
            >
              {TRIP_CONDITIONS.IS_VIEW_MODE(mode) && !isEmptyDay && (
                <div className='absolute top-0 bottom-0 bg-gray-300 w-1pxr left-42pxr'></div>
              )}

              {morningAccomm && (
                <DestSlot
                  key={morningAccomm.cur.id || `morning_day${dayNumber}`}
                  dest={morningAccomm}
                  mode={mode}
                  dayNumber={+dayNumber}
                  destIndex={0}
                  isEditRole={isEditRole}
                  isLastDay={isLastDay}
                  isLastDest={destListWithoutAccomms.length === 0 && !nightAccomm}
                  onClickPoiDetail={onClickPoiDetail}
                  setDestinations={setDestinations}
                  isSortableDragging={isSortableDragging}
                  destinations={destinations}
                  onClickChangeMode={onClickChangeMode}
                  setAddDestinationMode={setAddDestinationMode}
                  setAddDestinationsPosition={setAddDestinationsPosition}
                  onClickRemoveSlot={onClickRemoveSlot}
                  onToggleSelectDest={onToggleSelectDest}
                  onUpdateMemo={onUpdateMemo}
                  selectedDestIdList={selectedDestIdList}
                  memoDictionary={memoDictionary}
                />
              )}
              <div data-day={dayNumber}>
                <ReactSortable
                  className={`${isSortableDragging ? 'sortable-container' : ''}`}
                  group='dest-list'
                  animation={150}
                  handle='.dest-handle'
                  scroll={false}
                  scrollSensitivity={120}
                  scrollSpeed={12}
                  ghostClass='dest-ghost'
                  dragoverBubble={false}
                  forceFallback={true}
                  list={destListWithoutAccomms.map((dest) => {
                    return {
                      ...dest,
                      id: dest.cur.id ?? '',
                    };
                  })}
                  setList={(newList) => {}}
                  onChoose={(e: any) => {
                    setIsSortableDragging(true);
                  }}
                  onUnchoose={() => {
                    setIsSortableDragging(false);
                  }}
                  onEnd={(e) => {
                    setIsSortableDragging(false);
                    if (e.from === e.to && e.oldIndex === e.newIndex) return;
                    onSortableDragEnd(e);
                  }}
                >
                  {destListWithoutAccomms.map((dest, destIndex) => (
                    <DestSlot
                      key={dest.cur.id || `dest_day${dayNumber}_${destIndex}`}
                      dest={dest}
                      mode={mode}
                      dayNumber={+dayNumber}
                      destIndex={destIndex + (morningAccomm ? 1 : 0)}
                      isEditRole={isEditRole}
                      isLastDay={isLastDay}
                      isLastDest={destIndex === destListWithoutAccomms.length - 1 && !nightAccomm}
                      onClickPoiDetail={onClickPoiDetail}
                      setDestinations={setDestinations}
                      isSortableDragging={isSortableDragging}
                      destinations={destinations}
                      onClickChangeMode={onClickChangeMode}
                      setAddDestinationMode={setAddDestinationMode}
                      setAddDestinationsPosition={setAddDestinationsPosition}
                      onClickRemoveSlot={onClickRemoveSlot}
                      onToggleSelectDest={onToggleSelectDest}
                      onUpdateMemo={onUpdateMemo}
                      selectedDestIdList={selectedDestIdList}
                      memoDictionary={memoDictionary}
                    />
                  ))}
                </ReactSortable>
              </div>
              {nightAccomm && (
                <DestSlot
                  key={nightAccomm.cur.id || `night_day${dayNumber}`}
                  dest={nightAccomm}
                  mode={mode}
                  dayNumber={+dayNumber}
                  destIndex={destListWithoutAccomms.length + (morningAccomm ? 1 : 0)}
                  isEditRole={isEditRole}
                  isLastDay={isLastDay}
                  isLastDest
                  onClickPoiDetail={onClickPoiDetail}
                  setDestinations={setDestinations}
                  isSortableDragging={isSortableDragging}
                  destinations={destinations}
                  onClickChangeMode={onClickChangeMode}
                  setAddDestinationMode={setAddDestinationMode}
                  setAddDestinationsPosition={setAddDestinationsPosition}
                  onClickRemoveSlot={onClickRemoveSlot}
                  onToggleSelectDest={onToggleSelectDest}
                  onUpdateMemo={onUpdateMemo}
                  selectedDestIdList={selectedDestIdList}
                  memoDictionary={memoDictionary}
                  실험실짱={실험실짱}
                />
              )}

              {isEditRole && isEmptyDay && (
                <div className={`px-20pxr z-[1] ${TRIP_CONDITIONS.IS_VIEW_MODE(mode) ? 'py-4pxr' : 'py-0'}`}>
                  <button
                    className='flex items-center justify-center w-full bg-white border border-gray-200 h-40pxr gap-4pxr rounded-4pxr'
                    onClick={() => {
                      onClickChangeMode(TRIP_MODE.SEARCH);
                      setAddDestinationsPosition({
                        day: +dayNumber,
                        pos: 1,
                      });
                      setAddDestinationMode('normal');
                    }}
                  >
                    <AddGray1818 className='flex-none' />
                    <span className='text-gray-700 body-14-r'>장소 추가</span>
                  </button>
                </div>
              )}
            </div>
          </React.Fragment>
        );
      })}

      {실험실짱 && (
        <div className='relative mx-auto flex flex-col items-center z-10 mt-[32px]'>
          {!isFinishItinerarySummary && (
            <div className='w-full pl-32pxr pr-20pxr'>
              <button
                className={`neonsign-button w-full h-50pxr bg-point-100 rounded-8pxr text-point-400 text-[16px] font-bold relative`}
                onClick={onClickItinerarySummaryHandler}
              >
                일정표 AI 요약
              </button>
            </div>
          )}

          <div
            ref={itinerarySummaryContainerRef}
            className='w-full pl-32pxr pr-20pxr'
            style={{
              height: 0,
              overflow: 'hidden',
            }}
          >
            <div className='w-full relative'>
              <div
                ref={itinerarySummaryRef}
                className={`neonsign-button text-gray-700 body-14-r bg-point-100 rounded-8pxr p-10pxr`}
                style={{
                  minHeight: '40px',
                }}
              ></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
